import React, { useState, useEffect } from 'react';
import { FIELD_TYPES, DATA_SOURCE_TYPES } from '../../config/formTemplates';
// import ActionSelector from '../ActionSystem/ActionSelector'; // REMOVED: Old ActionSystem (2025-11-20)
// import actionRegistry from '../ActionSystem/ActionRegistry'; // REMOVED: Old ActionSystem (2025-11-20)
// import '../ActionSystem/actions.css'; // REMOVED: Old ActionSystem (2025-11-20)

const FormEditor = ({ 
  formData, 
  onFormUpdate, 
  showHeader = true,
  compact = false 
}) => {
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState({});
  const [showActionSelector, setShowActionSelector] = useState(false);

  // Initialize form data if not provided
  const currentFormData = formData || {
    title: 'New Form',
    description: '',
    submitLabel: 'Submit',
    fields: [],
    actions: [],
    settings: {
      showProgress: false,
      allowDraft: false,
      requireAuth: false,
      successMessage: 'Form submitted successfully!',
      errorMessage: 'Please fix the errors and try again.',
      redirectUrl: ''
    }
  };

  // Update form field
  const updateFormField = (path, value) => {
    const newFormData = { ...currentFormData };
    const keys = path.split('.');
    let current = newFormData;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    onFormUpdate(newFormData);
  };

  // Update specific field
  const updateField = (fieldIndex, fieldPath, value) => {
    const newFields = [...currentFormData.fields];
    const keys = fieldPath.split('.');
    let current = newFields[fieldIndex];
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    
    // Auto-generate field ID when label changes
    if (fieldPath === 'label') {
      current.id = value.toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 30) || `field_${Date.now()}`;
    }
    
    updateFormField('fields', newFields);
  };

  // Add new field
  const addField = (type = 'text') => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      type: type,
      required: false,
      validation: {
        customMessage: ''
      },
      dataSource: { type: 'manual' },
      visibility: {
        show: true,
        conditions: []
      },
      styling: {
        width: '100%',
        placeholder: ''
      },
      defaultValue: '',
      helpText: ''
    };
    
    updateFormField('fields', [...currentFormData.fields, newField]);
    setActiveFieldId(newField.id);
  };

  // Remove field
  const removeField = (fieldIndex) => {
    const newFields = currentFormData.fields.filter((_, index) => index !== fieldIndex);
    updateFormField('fields', newFields);
    setActiveFieldId(null);
  };

  // Move field
  const moveField = (fromIndex, toIndex) => {
    const newFields = [...currentFormData.fields];
    const [movedField] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, movedField);
    updateFormField('fields', newFields);
  };

  // Add visibility condition
  const addVisibilityCondition = (fieldIndex) => {
    const field = currentFormData.fields[fieldIndex];
    const newCondition = {
      fieldId: '',
      operator: 'equals',
      value: '',
      action: 'show'
    };
    
    const conditions = field.visibility?.conditions || [];
    updateField(fieldIndex, 'visibility.conditions', [...conditions, newCondition]);
  };

  // Update visibility condition
  const updateVisibilityCondition = (fieldIndex, conditionIndex, property, value) => {
    const field = currentFormData.fields[fieldIndex];
    const conditions = [...(field.visibility?.conditions || [])];
    conditions[conditionIndex] = { ...conditions[conditionIndex], [property]: value };
    updateField(fieldIndex, 'visibility.conditions', conditions);
  };

  // Remove visibility condition
  const removeVisibilityCondition = (fieldIndex, conditionIndex) => {
    const field = currentFormData.fields[fieldIndex];
    const conditions = (field.visibility?.conditions || []).filter((_, idx) => idx !== conditionIndex);
    updateField(fieldIndex, 'visibility.conditions', conditions);
  };

  // Toggle advanced settings for field
  const toggleAdvanced = (fieldId) => {
    setShowAdvanced(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  // Action handlers
  const addAction = (actionData) => {
    const newActions = [...(currentFormData.actions || []), actionData];
    updateFormField('actions', newActions);
    setShowActionSelector(false);
  };

  const removeAction = (index) => {
    const newActions = currentFormData.actions.filter((_, i) => i !== index);
    updateFormField('actions', newActions);
  };

  const moveAction = (index, direction) => {
    const newActions = [...currentFormData.actions];
    const newIndex = index + direction;
    
    if (newIndex >= 0 && newIndex < newActions.length) {
      [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];
      updateFormField('actions', newActions);
    }
  };

  const editAction = (index) => {
    // For now, just show action selector with existing config
    // In a full implementation, you'd open the config form with existing data
    console.log('Edit action:', currentFormData.actions[index]);
    // TODO: Implement action editing
  };

  return (
    <div className={`form-editor ${compact ? 'form-editor--compact' : ''}`}>
      {showHeader && (
        <div className="form-editor__header">
          <h3>📋 Form Editor</h3>
          <p>Configure form fields, validation, and behavior</p>
        </div>
      )}

      {/* Form Settings */}
      <div className="form-editor__section">
        <h4>Form Settings</h4>
        
        <div className="form-editor__grid">
          <div className="form-editor__field">
            <label>Form Title</label>
            <input
              type="text"
              value={currentFormData.title}
              onChange={(e) => updateFormField('title', e.target.value)}
              placeholder="Enter form title"
            />
          </div>

          <div className="form-editor__field">
            <label>Description</label>
            <textarea
              value={currentFormData.description}
              onChange={(e) => updateFormField('description', e.target.value)}
              placeholder="Form description (optional)"
              rows={2}
            />
          </div>

          <div className="form-editor__field">
            <label>Submit Button Text</label>
            <input
              type="text"
              value={currentFormData.submitLabel}
              onChange={(e) => updateFormField('submitLabel', e.target.value)}
              placeholder="Submit"
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions-section">
        <div className="form-actions-header">
          <h3>Form Actions</h3>
          <button 
            type="button"
            className="add-action-btn"
            onClick={() => setShowActionSelector(true)}
          >
            + Add Action
          </button>
        </div>

        {currentFormData.actions && currentFormData.actions.length > 0 ? (
          <div className="form-actions-list">
            {currentFormData.actions.map((action, index) => {
              // Fixed by Claude on 2025-11-23 - Handle actions without actionRegistry
              const actionName = action.actionName || action.action || 'Unknown Action';
              const actionIcon = action.actionIcon || '⚡';
              const actionSummary = action.description || action.summary || `${actionName} action`;

              return (
                <div key={index} className="form-action-item">
                  <div className="form-action-order">{index + 1}</div>
                  <div className="form-action-icon">{actionIcon}</div>
                  <div className="form-action-details">
                    <div className="form-action-name">{actionName}</div>
                    <div className="form-action-summary">{actionSummary}</div>
                  </div>
                  <div className="form-action-controls">
                    <button 
                      type="button"
                      className="form-action-btn"
                      onClick={() => moveAction(index, -1)}
                      disabled={index === 0}
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button 
                      type="button"
                      className="form-action-btn"
                      onClick={() => moveAction(index, 1)}
                      disabled={index === currentFormData.actions.length - 1}
                      title="Move Down"
                    >
                      ↓
                    </button>
                    <button 
                      type="button"
                      className="form-action-btn"
                      onClick={() => editAction(index)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      type="button"
                      className="form-action-btn delete"
                      onClick={() => removeAction(index)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-actions">
            <div className="no-actions-icon">⚡</div>
            <p>No actions configured</p>
            <p>Actions define what happens when the form is submitted</p>
          </div>
        )}
      </div>

      {/* Form Fields */}
      <div className="form-editor__section">
        <div className="form-editor__section-header">
          <h4>Form Fields ({currentFormData.fields.length})</h4>
          <div className="form-editor__actions">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  addField(e.target.value);
                  e.target.value = '';
                }
              }}
              value=""
            >
              <option value="">+ Add Field</option>
              {FIELD_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {currentFormData.fields.length === 0 ? (
          <div className="form-editor__empty">
            <p>No fields added yet. Use the dropdown above to add your first field.</p>
          </div>
        ) : (
          <div className="form-editor__fields">
            {currentFormData.fields.map((field, index) => (
              <div 
                key={field.id} 
                className={`form-editor__field-card ${activeFieldId === field.id ? 'active' : ''}`}
              >
                {/* Field Header */}
                <div 
                  className="form-editor__field-header"
                  onClick={() => setActiveFieldId(activeFieldId === field.id ? null : field.id)}
                >
                  <div className="form-editor__field-info">
                    <span className="form-editor__field-label">{field.label}</span>
                    <span className="form-editor__field-type">{field.type}</span>
                    {field.required && <span className="form-editor__field-required">*</span>}
                  </div>
                  
                  <div className="form-editor__field-controls">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (index > 0) moveField(index, index - 1);
                      }}
                      disabled={index === 0}
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (index < currentFormData.fields.length - 1) moveField(index, index + 1);
                      }}
                      disabled={index === currentFormData.fields.length - 1}
                      title="Move Down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeField(index);
                      }}
                      title="Delete Field"
                      className="form-editor__delete-btn"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Field Details */}
                {activeFieldId === field.id && (
                  <div className="form-editor__field-details">
                    {/* Basic Properties */}
                    <div className="form-editor__field-section">
                      <h5>Basic Properties</h5>
                      <div className="form-editor__field-grid">
                        <div className="form-editor__field-item">
                          <label>Label</label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(index, 'label', e.target.value)}
                          />
                        </div>

                        <div className="form-editor__field-item">
                          <label>Field ID</label>
                          <input
                            type="text"
                            value={field.id}
                            onChange={(e) => updateField(index, 'id', e.target.value)}
                            className="form-editor__field-readonly"
                            readOnly
                          />
                        </div>

                        <div className="form-editor__field-item">
                          <label>Field Type</label>
                          <select
                            value={field.type}
                            onChange={(e) => updateField(index, 'type', e.target.value)}
                          >
                            {FIELD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-editor__field-item">
                          <label>Placeholder</label>
                          <input
                            type="text"
                            value={field.styling?.placeholder || ''}
                            onChange={(e) => updateField(index, 'styling.placeholder', e.target.value)}
                            placeholder="Enter placeholder text"
                          />
                        </div>

                        <div className="form-editor__field-item">
                          <label>Help Text</label>
                          <input
                            type="text"
                            value={field.helpText || ''}
                            onChange={(e) => updateField(index, 'helpText', e.target.value)}
                            placeholder="Additional help text"
                          />
                        </div>

                        <div className="form-editor__field-item">
                          <label>Default Value</label>
                          <input
                            type="text"
                            value={field.defaultValue || ''}
                            onChange={(e) => updateField(index, 'defaultValue', e.target.value)}
                            placeholder="Default field value"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Field States */}
                    <div className="form-editor__field-section">
                      <h5>Field States</h5>
                      <div className="form-editor__field-checkboxes">
                        <label className="form-editor__checkbox">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => updateField(index, 'required', e.target.checked)}
                          />
                          Required
                        </label>

                        <label className="form-editor__checkbox">
                          <input
                            type="checkbox"
                            checked={field.visibility?.show !== false}
                            onChange={(e) => updateField(index, 'visibility.show', e.target.checked)}
                          />
                          Visible
                        </label>

                        <label className="form-editor__checkbox">
                          <input
                            type="checkbox"
                            checked={field.disabled || false}
                            onChange={(e) => updateField(index, 'disabled', e.target.checked)}
                          />
                          Disabled
                        </label>

                        <label className="form-editor__checkbox">
                          <input
                            type="checkbox"
                            checked={field.readonly || false}
                            onChange={(e) => updateField(index, 'readonly', e.target.checked)}
                          />
                          Read Only
                        </label>
                      </div>
                    </div>

                    {/* Data Source (for dropdowns, radio, etc.) */}
                    {(field.type === 'dropdown' || field.type === 'radio') && (
                      <div className="form-editor__field-section">
                        <h5>Data Source</h5>
                        <div className="form-editor__field-grid">
                          <div className="form-editor__field-item">
                            <label>Source Type</label>
                            <select
                              value={field.dataSource?.type || 'manual'}
                              onChange={(e) => updateField(index, 'dataSource.type', e.target.value)}
                            >
                              {DATA_SOURCE_TYPES.map(source => (
                                <option key={source.value} value={source.value}>
                                  {source.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {field.dataSource?.type === 'manual' && (
                            <div className="form-editor__field-item form-editor__field-item--full">
                              <label>Options (one per line)</label>
                              <textarea
                                value={(field.dataSource?.options || []).join('\n')}
                                onChange={(e) => {
                                  const options = e.target.value.split('\n').filter(opt => opt.trim());
                                  updateField(index, 'dataSource.options', options);
                                }}
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                                rows={4}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Advanced Settings Toggle */}
                    <div className="form-editor__field-section">
                      <button
                        type="button"
                        className="form-editor__toggle-advanced"
                        onClick={() => toggleAdvanced(field.id)}
                      >
                        {showAdvanced[field.id] ? '▼' : '▶'} Advanced Settings
                      </button>

                      {showAdvanced[field.id] && (
                        <div className="form-editor__advanced">
                          {/* Validation Rules */}
                          <div className="form-editor__subsection">
                            <h6>Validation Rules</h6>
                            <div className="form-editor__field-grid">
                              <div className="form-editor__field-item">
                                <label>Min Length</label>
                                <input
                                  type="number"
                                  value={field.validation?.minLength || ''}
                                  onChange={(e) => updateField(index, 'validation.minLength', parseInt(e.target.value) || undefined)}
                                  min="0"
                                />
                              </div>

                              <div className="form-editor__field-item">
                                <label>Max Length</label>
                                <input
                                  type="number"
                                  value={field.validation?.maxLength || ''}
                                  onChange={(e) => updateField(index, 'validation.maxLength', parseInt(e.target.value) || undefined)}
                                  min="0"
                                />
                              </div>

                              <div className="form-editor__field-item form-editor__field-item--full">
                                <label>Pattern (Regex)</label>
                                <input
                                  type="text"
                                  value={field.validation?.pattern || ''}
                                  onChange={(e) => updateField(index, 'validation.pattern', e.target.value)}
                                  placeholder="^[a-zA-Z0-9]+$"
                                />
                              </div>

                              <div className="form-editor__field-item form-editor__field-item--full">
                                <label>Custom Error Message</label>
                                <input
                                  type="text"
                                  value={field.validation?.customMessage || ''}
                                  onChange={(e) => updateField(index, 'validation.customMessage', e.target.value)}
                                  placeholder="Please enter a valid value"
                                />
                              </div>

                              {field.type === 'password' && (
                                <div className="form-editor__field-item">
                                  <label>Match Field</label>
                                  <select
                                    value={field.validation?.matchField || ''}
                                    onChange={(e) => updateField(index, 'validation.matchField', e.target.value)}
                                  >
                                    <option value="">Select field to match</option>
                                    {currentFormData.fields
                                      .filter(f => f.id !== field.id && f.type === 'password')
                                      .map(f => (
                                        <option key={f.id} value={f.id}>{f.label}</option>
                                      ))
                                    }
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Visibility Conditions */}
                          <div className="form-editor__subsection">
                            <div className="form-editor__subsection-header">
                              <h6>Visibility Conditions</h6>
                              <button
                                type="button"
                                onClick={() => addVisibilityCondition(index)}
                                className="form-editor__add-condition"
                              >
                                + Add Condition
                              </button>
                            </div>

                            {field.visibility?.conditions?.map((condition, conditionIndex) => (
                              <div key={conditionIndex} className="form-editor__condition">
                                <select
                                  value={condition.action || 'show'}
                                  onChange={(e) => updateVisibilityCondition(index, conditionIndex, 'action', e.target.value)}
                                  className="form-editor__condition-action"
                                >
                                  <option value="show">Show</option>
                                  <option value="hide">Hide</option>
                                  <option value="enable">Enable</option>
                                  <option value="disable">Disable</option>
                                </select>

                                <span>this field when</span>

                                <select
                                  value={condition.fieldId || ''}
                                  onChange={(e) => updateVisibilityCondition(index, conditionIndex, 'fieldId', e.target.value)}
                                  className="form-editor__condition-field"
                                >
                                  <option value="">Select field</option>
                                  {currentFormData.fields
                                    .filter(f => f.id !== field.id)
                                    .map(f => (
                                      <option key={f.id} value={f.id}>{f.label}</option>
                                    ))
                                  }
                                </select>

                                <select
                                  value={condition.operator || 'equals'}
                                  onChange={(e) => updateVisibilityCondition(index, conditionIndex, 'operator', e.target.value)}
                                  className="form-editor__condition-operator"
                                >
                                  <option value="equals">equals</option>
                                  <option value="not_equals">not equals</option>
                                  <option value="contains">contains</option>
                                  <option value="not_contains">not contains</option>
                                  <option value="empty">is empty</option>
                                  <option value="not_empty">is not empty</option>
                                </select>

                                {!['empty', 'not_empty'].includes(condition.operator) && (
                                  <input
                                    type="text"
                                    value={condition.value || ''}
                                    onChange={(e) => updateVisibilityCondition(index, conditionIndex, 'value', e.target.value)}
                                    placeholder="Value"
                                    className="form-editor__condition-value"
                                  />
                                )}

                                <button
                                  type="button"
                                  onClick={() => removeVisibilityCondition(index, conditionIndex)}
                                  className="form-editor__remove-condition"
                                  title="Remove Condition"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}

                            {(!field.visibility?.conditions || field.visibility.conditions.length === 0) && (
                              <p className="form-editor__no-conditions">
                                No visibility conditions set. Field will always be visible.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Settings */}
      <div className="form-editor__section">
        <h4>Form Settings</h4>
        <div className="form-editor__field-checkboxes">
          <label className="form-editor__checkbox">
            <input
              type="checkbox"
              checked={currentFormData.settings?.showProgress || false}
              onChange={(e) => updateFormField('settings.showProgress', e.target.checked)}
            />
            Show Progress Bar
          </label>

          <label className="form-editor__checkbox">
            <input
              type="checkbox"
              checked={currentFormData.settings?.allowDraft || false}
              onChange={(e) => updateFormField('settings.allowDraft', e.target.checked)}
            />
            Allow Save as Draft
          </label>

          <label className="form-editor__checkbox">
            <input
              type="checkbox"
              checked={currentFormData.settings?.requireAuth || false}
              onChange={(e) => updateFormField('settings.requireAuth', e.target.checked)}
            />
            Require Authentication
          </label>
        </div>
      </div>

      {/* Action Selector Modal */}
      {showActionSelector && (
        <ActionSelector
          onActionAdd={addAction}
          onClose={() => setShowActionSelector(false)}
          existingActions={currentFormData.actions || []}
        />
      )}
    </div>
  );
};

export default FormEditor;
